'use client';
import React, { useState } from 'react';
import { AlertTriangle, X, ChevronRight, Zap, Eye } from 'lucide-react';

interface AIAlert {
  id: string;
  severity: 'critical' | 'high' | 'medium';
  type: string;
  description: string;
  entity: string;
  county: string;
  time: string;
  dismissed: boolean;
}

// Backend integration point: replace with API call to /api/ai/fraud-alerts
const initialAlerts: AIAlert[] = [
  {
    id: 'alert-001',
    severity: 'critical',
    type: 'Duplicate Transaction',
    description: 'Same TIN filed identical $2.4M GST return from 3 different IP addresses within 4 minutes.',
    entity: 'Kollie & Sons Trading Co.',
    county: 'Montserrado',
    time: '01:14 AM',
    dismissed: false,
  },
  {
    id: 'alert-002',
    severity: 'critical',
    type: 'Under-Invoicing Detected',
    description: 'Customs declaration value $180K vs estimated market value $640K for electronics shipment.',
    entity: 'CONT-2026-08841',
    county: 'Freeport',
    time: '12:58 AM',
    dismissed: false,
  },
  {
    id: 'alert-003',
    severity: 'high',
    type: 'Ghost Receipt Pattern',
    description: 'Field officer BO-0042 issued 34 receipts in 8 minutes — physically impossible.',
    entity: 'Officer: M. Pewee',
    county: 'Nimba',
    time: '12:31 AM',
    dismissed: false,
  },
  {
    id: 'alert-004',
    severity: 'high',
    type: 'Revenue Anomaly',
    description: 'Lofa County collections 44% below 30-day moving average — unexplained gap.',
    entity: 'Lofa County Office',
    county: 'Lofa',
    time: 'Yesterday',
    dismissed: false,
  },
  {
    id: 'alert-005',
    severity: 'medium',
    type: 'Taxpayer Risk Spike',
    description: 'TIN LR-0082741 risk score jumped from 32 to 87 after large asset transfer detected.',
    entity: 'TIN: LR-0082741',
    county: 'Margibi',
    time: 'Yesterday',
    dismissed: false,
  },
  {
    id: 'alert-006',
    severity: 'medium',
    type: 'Unregistered Property',
    description: 'AI GIS scan detected 14 new structures in Paynesville with no property tax records.',
    entity: 'Zone: Paynesville North',
    county: 'Montserrado',
    time: 'May 27',
    dismissed: false,
  },
];

const severityConfig = {
  critical: {
    color: '#fca5a5',
    bg: 'rgba(220,38,38,0.15)',
    border: 'rgba(220,38,38,0.3)',
    label: 'CRITICAL',
  },
  high: {
    color: '#fb923c',
    bg: 'rgba(251,146,60,0.12)',
    border: 'rgba(251,146,60,0.25)',
    label: 'HIGH',
  },
  medium: {
    color: 'var(--accent)',
    bg: 'rgba(212,160,23,0.1)',
    border: 'rgba(212,160,23,0.2)',
    label: 'MEDIUM',
  },
};

export default function AIAlertFeed() {
  const [alerts, setAlerts] = useState(initialAlerts);

  const dismiss = (id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, dismissed: true } : a))
    );
  };

  const visible = alerts.filter((a) => !a.dismissed);

  return (
    <div
      className="rounded-xl p-4 h-full flex flex-col"
      style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap size={16} style={{ color: 'var(--accent)' }} />
          <h3 className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
            AI Fraud & Risk Alerts
          </h3>
        </div>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ backgroundColor: 'rgba(220,38,38,0.2)', color: '#fca5a5' }}
        >
          {visible.length} active
        </span>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin space-y-2">
        {visible.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(74,222,128,0.1)' }}
            >
              <AlertTriangle size={20} style={{ color: '#4ade80' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--dark-text)' }}>
              No active alerts
            </p>
            <p className="text-xs text-center" style={{ color: 'var(--dark-muted)' }}>
              AI monitoring is active — all fraud signals resolved
            </p>
          </div>
        )}
        {visible.map((alert) => {
          const cfg = severityConfig[alert.severity];
          return (
            <div
              key={alert.id}
              className="rounded-lg p-3 transition-all duration-200 animate-slide-up"
              style={{
                backgroundColor: cfg.bg,
                border: `1px solid ${cfg.border}`,
              }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="text-xs font-bold tracking-wide"
                      style={{ color: cfg.color }}
                    >
                      {cfg.label}
                    </span>
                    <span className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>
                      {alert.type}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed mb-1.5" style={{ color: 'var(--dark-muted)' }}>
                    {alert.description}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-xs font-mono-data px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: 'var(--dark-text)' }}
                    >
                      {alert.entity}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                      {alert.county} · {alert.time}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    className="p-1 rounded transition-all hover:bg-white/10"
                    style={{ color: 'var(--dark-muted)' }}
                    title="View case details"
                    aria-label="View alert details"
                  >
                    <Eye size={13} />
                  </button>
                  <button
                    onClick={() => dismiss(alert.id)}
                    className="p-1 rounded transition-all hover:bg-white/10"
                    style={{ color: 'var(--dark-muted)' }}
                    title="Dismiss alert"
                    aria-label="Dismiss alert"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all duration-150 hover:bg-white/10"
        style={{
          color: 'var(--accent)',
          border: '1px solid rgba(212,160,23,0.2)',
        }}
      >
        View All 47 Alerts
        <ChevronRight size={13} />
      </button>
    </div>
  );
}